import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const FluVaccinationForm = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(true);
  const [fluHistory, setFluHistory] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState("");

  const positions = [
    "RN",
    "PT",
    "PTA",
    "OT",
    "OTA",
    "ST",
    "HHA",
    "LVN",
    "MSW",
    "Caregiver",
    "CNA",
    "Office Assistant",
    "ADMIN"
  ];

  const positionMap = {
    "RN": "Registered Nurse",
    "PT": "Physical Therapist",
    "PTA": "Physical Therapist Assistant",
    "OT": "Occupational Therapist",
    "OTA": "Occupational Therapist Assistant",
    "ST": "Speech Language Pathologist",
    "HHA": "Certified Home Health Aide",
    "LVN": "Licensed Vocational Nurse",
    "MSW": "Medical Social Worker",
    "ADMIN": "Administrator",
    "Office Assistant": "Office Assistant"
  };

  const [fluFormData, setFluFormData] = useState({
    employeeId: "",
    employeeName: "",
    // Clinician Information
    clinicianName: "",
    clinicianTitle: "",
    // Refusal Acknowledgment
    refusalAcknowledgmentSignature: "",
    refusalAcknowledgmentDate: "",
    // Consent Section
    printName: "",
    clinicianSignature: "",
    locationProvidingVaccine: "",
    siteOfInjection: "",
    administeredByMD: false,
    administeredByRN: false,
    administeredByName: "",
    lotNumber: "",
    expirationDate: "",
    // Vaccination Decision
    alreadyReceived: false,
    alreadyReceivedLocation: "",
    alreadyReceivedDate: "",
    wouldLikeToReceive: false,
    refuseDecline: false,
    refuseReasonAllergies: false,
    refuseReasonIllness: false,
    refuseReasonReligious: false,
    refuseReasonBeliefs: false,
    refuseReasonPersonalPreference: false,
    refuseReasonOther: false,
    refuseReasonOtherText: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      // Exclude file data - we only need user list for selection
      const data = await getUsers(true);
      setUsers(data);
      setLoading(false);
    };
    loadUsers();

    // Load flu history from localStorage
    const savedHistory = localStorage.getItem("fluHistory");
    if (savedHistory) {
      setFluHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleInputChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    let fieldName = name || id;

    // If position is selected
    if (id === "selectedPosition") {
      setSelectedPosition(value);
      setFluFormData(prev => ({
        ...prev,
        employeeId: "",
        employeeName: ""
      }));
      return;
    }

    // If employee is selected, auto-populate name and position
    if (id === "employeeId") {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        const fullName = `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim();
        const empPosition = selectedUser.position || "";
        const empPositionFull = positionMap[empPosition] || empPosition;
        setFluFormData(prev => ({
          ...prev,
          employeeId: value,
          employeeName: fullName,
          printName: fullName,
          clinicianName: fullName, // Auto-populate clinician name
          clinicianTitle: empPositionFull // Auto-populate clinician title with full position name
        }));
        return;
      }
    }

    setFluFormData(prev => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value
    }));
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    try {
      let date;
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month} / ${day} / ${year}`;
    } catch {
      return "";
    }
  };

  const generateFluPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Flu Vaccination Program - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 8.5pt;
            line-height: 1.3;
            padding: 12px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 10px;
            border-bottom: 3px solid #2c3e50;
            min-height: 100px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 20px;
          }
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 8pt;
            text-align: right;
            line-height: 1.4;
          }
          .section {
            margin-bottom: 14px;
          }
          .section-title {
            font-size: 10.5pt;
            font-weight: bold;
            margin: 12px 0 8px 0;
            color: #2c3e50;
            text-transform: uppercase;
            text-align: center;
          }
          .divider {
            border-top: 2px dashed #333;
            margin: 12px 0;
            text-align: center;
            padding-top: 6px;
          }
          .divider-text {
            font-size: 8pt;
            color: #666;
          }
          .info-row {
            display: flex;
            margin-bottom: 8px;
            font-size: 8.5pt;
          }
          .info-label {
            font-weight: 600;
            color: #495057;
            min-width: 140px;
          }
          .info-value {
            border-bottom: 1px solid #495057;
            flex: 1;
            min-height: 18px;
            padding-bottom: 2px;
            color: #212529;
          }
          .statement-text {
            font-size: 8.5pt;
            line-height: 1.5;
            margin-bottom: 10px;
            color: #333;
          }
          .bullet-list {
            margin-left: 15px;
            margin-bottom: 10px;
          }
          .bullet-item {
            font-size: 8.5pt;
            line-height: 1.5;
            margin-bottom: 5px;
          }
          .checkbox-group {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;
            font-size: 8.5pt;
          }
          .checkbox-label {
            color: #495057;
          }
          .checkbox-inline {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-right: 12px;
            font-size: 8.5pt;
          }
          .two-column {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 10px;
          }
          .signature-row {
            display: flex;
            gap: 20px;
            margin-top: 10px;
            margin-bottom: 10px;
          }
          .signature-field {
            flex: 1;
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 3px;
            font-size: 8.5pt;
          }
          .signature-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 2px;
            color: #212529;
            font-size: 8.5pt;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 12px;
            height: 12px;
            margin: 0;
            vertical-align: middle;
          }
          .indent {
            margin-left: 20px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>FLU VACCINATION PROGRAM</h1>
          </div>
        </div>

        <div class="section">
          <div class="info-row">
            <span class="info-label">CLINICIAN NAME:</span>
            <span class="info-value">${data.clinicianName || "________________"}</span>
          </div>
          <div class="info-row">
            <span class="info-label">TITLE:</span>
            <span class="info-value">${data.clinicianTitle || "________________"}</span>
          </div>
        </div>

        <div class="section">
          <div class="statement-text">
            Infinite Home Health has advised me that I should receive the Influenza (Flu) Vaccine.
          </div>
          <div class="statement-text" style="font-weight: 600; margin-top: 6px;">
            I understand:
          </div>
          <div class="bullet-list">
            <div class="bullet-item">• The purpose of and the need for the recommended vaccine.</div>
            <div class="bullet-item">• The risk and benefits of the recommended vaccine.</div>
            <div class="bullet-item">• If I do not receive that vaccine, the consequences may include increased risk of:</div>
            <div class="indent">
              <div class="bullet-item">→ Getting sick from the illness the vaccine could prevent</div>
              <div class="bullet-item">→ Being hospitalized for heart disease, stroke, pneumonia, or influenza, if I am 65 years of age or older.</div>
              <div class="bullet-item">→ Spreading the disease to others who become ill, be hospitalized, or die as a result</div>
              <div class="bullet-item">→ Death, if I am 65 years of age or older</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="statement-text">
            Nevertheless, I have decided to refuse/receive the vaccine recommended above by checking the following appropriate box:
          </div>
          
          <div class="checkbox-group">
            <input type="checkbox" ${data.alreadyReceived ? "checked" : ""} disabled>
            <span class="checkbox-label">Already Received Vaccination:</span>
          </div>
          ${data.alreadyReceived ? `
            <div class="indent" style="margin-bottom: 6px;">
              <span style="font-size: 8.5pt;">Location: <span style="border-bottom: 1px solid #495057; min-width: 150px; display: inline-block;">${data.alreadyReceivedLocation || ""}</span></span>
              <span style="margin-left: 15px; font-size: 8.5pt;">Date: <span style="border-bottom: 1px solid #495057; min-width: 100px; display: inline-block;">${formatDateShort(data.alreadyReceivedDate) || ""}</span></span>
            </div>
          ` : ""}
          
          <div class="checkbox-group">
            <input type="checkbox" ${data.wouldLikeToReceive ? "checked" : ""} disabled>
            <span class="checkbox-label">Would like to receive Influenza Vaccination through Infinite Home Health (Fee $15.00)</span>
          </div>
          
          <div class="checkbox-group">
            <input type="checkbox" ${data.refuseDecline ? "checked" : ""} disabled>
            <span class="checkbox-label">Refuse/Decline to Receive Influenza Vaccination due to:</span>
          </div>
          ${data.refuseDecline ? `
            <div class="indent">
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonAllergies ? "checked" : ""} disabled>
                <span>Allergies</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonIllness ? "checked" : ""} disabled>
                <span>Illness</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonReligious ? "checked" : ""} disabled>
                <span>Religious</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonBeliefs ? "checked" : ""} disabled>
                <span>Beliefs</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonPersonalPreference ? "checked" : ""} disabled>
                <span>Personal Preference</span>
              </div>
              <div class="checkbox-inline">
                <input type="checkbox" ${data.refuseReasonOther ? "checked" : ""} disabled>
                <span>Other: <span style="border-bottom: 1px solid #495057; min-width: 200px; display: inline-block;">${data.refuseReasonOtherText || ""}</span></span>
              </div>
            </div>
          ` : ""}
        </div>

        <div class="section">
          <div class="statement-text" style="margin-bottom: 8px;">
            I know that my failure to follow these recommendations for vaccination may endanger my health or the health of people I come in contact with. I know that, even though I refuse to be vaccinated now, I can change my mind at any time and accept vaccination in the future. I acknowledge that I have read this Flu Vaccination form in its entirety and fully understand it.
          </div>
          <div class="signature-row">
            <div class="signature-field">
              <div class="signature-label">Signature:</div>
              <div class="signature-value">${data.refusalAcknowledgmentSignature || "________________"}</div>
            </div>
            <div class="signature-field">
              <div class="signature-label">Date:</div>
              <div class="signature-value">${formatDateShort(data.refusalAcknowledgmentDate) || "________________"}</div>
            </div>
          </div>
        </div>

        <div class="divider">
          <div class="divider-text">---Office Use Only---</div>
        </div>

        <div class="section-title">.. FLU VACCINATION CONSENT ..</div>

        <div class="section">
          <div class="statement-text" style="margin-bottom: 8px;">
            I consent to the administration of the Influenza Vaccine, and am aware that some people experience pain at the site of the injection and some may experience fever. I testify that I have none of the following conditions: severe allergy to hen's eggs, severe reaction to flu shot in the past, history of Guillain-Barre Syndrome (GBS) in the 6 weeks after getting a flu shot,
          </div>
          
          <div class="two-column">
            <div class="info-row">
              <span class="info-label">Print Name:</span>
              <span class="info-value">${data.printName || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Clinician Signature:</span>
              <span class="info-value">${data.clinicianSignature || "________________"}</span>
            </div>
          </div>
          
          <div class="info-row">
            <span class="info-label">Location Providing Influenza Vaccine:</span>
            <span class="info-value">${data.locationProvidingVaccine || "________________"}</span>
          </div>
          
          <div class="two-column">
            <div class="info-row">
              <span class="info-label">Site of Injection:</span>
              <span class="info-value">${data.siteOfInjection || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Administered by:</span>
              <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.administeredByMD ? "checked" : ""} disabled>
                  <span>MD</span>
                </span>
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.administeredByRN ? "checked" : ""} disabled>
                  <span>RN</span>
                </span>
                <span style="border-bottom: 1px solid #495057; flex: 1; min-height: 18px; padding-bottom: 2px;">${data.administeredByName || ""}</span>
              </div>
            </div>
          </div>
          
          <div class="two-column">
            <div class="info-row">
              <span class="info-label">Lot #:</span>
              <span class="info-value">${data.lotNumber || "________________"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Expiration Date:</span>
              <span class="info-value">${formatDateShort(data.expirationDate) || "________________"}</span>
            </div>
          </div>
          
          <div class="info-row">
            <span class="info-label">Clinician's Signature (administering vaccination):</span>
            <span class="info-value">${data.clinicianSignature || "________________"}</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const printFluForm = (data) => {
    const printHTML = generateFluPrintFormHTML(data);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...fluFormData };

    // Save to flu history
    const historyEntry = {
      id: Date.now(),
      employeeName: fluFormData.employeeName,
      employeeId: fluFormData.employeeId,
      submittedAt: new Date().toISOString(),
      data: submitData
    };

    const updatedHistory = [historyEntry, ...fluHistory];
    setFluHistory(updatedHistory);
    localStorage.setItem('fluHistory', JSON.stringify(updatedHistory));

    // Automatically trigger print, then reset form
    setTimeout(() => {
      printFluForm(submitData);
      resetForm();
      setSelectedPosition("");
      setShowForm(false);
      setShowUserList(true);
    }, 300);
  };

  const resetForm = () => {
    setFluFormData({
      employeeId: "",
      employeeName: "",
      clinicianName: "",
      clinicianTitle: "",
      refusalAcknowledgmentSignature: "",
      refusalAcknowledgmentDate: "",
      printName: "",
      clinicianSignature: "",
      locationProvidingVaccine: "",
      siteOfInjection: "",
      administeredByMD: false,
      administeredByRN: false,
      administeredByName: "",
      lotNumber: "",
      expirationDate: "",
      alreadyReceived: false,
      alreadyReceivedLocation: "",
      alreadyReceivedDate: "",
      wouldLikeToReceive: false,
      refuseDecline: false,
      refuseReasonAllergies: false,
      refuseReasonIllness: false,
      refuseReasonReligious: false,
      refuseReasonBeliefs: false,
      refuseReasonPersonalPreference: false,
      refuseReasonOther: false,
      refuseReasonOtherText: "",
    });
    setSelectedPosition("");
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Flu Vaccination Program</h1>
        <button
          onClick={() => navigate("/forms")}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          Back to Forms
        </button>
      </div>

      {showUserList && !showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Select Employee</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position *
            </label>
            <select
              id="selectedPosition"
              value={selectedPosition}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select a position...</option>
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee Name *
            </label>
            <input
              type="text"
              name="employeeName"
              value={fluFormData.employeeName || ""}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="Enter employee name"
              required
            />
          </div>
          <button
            onClick={() => {
              if (!selectedPosition) {
                alert("Please select a position");
                return;
              }
              if (!fluFormData.employeeName || fluFormData.employeeName.trim() === "") {
                alert("Please enter employee name");
                return;
              }
              setShowUserList(false);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            disabled={!selectedPosition || !fluFormData.employeeName || fluFormData.employeeName.trim() === ""}
          >
            Continue
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Information Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <input
                    type="text"
                    name="employeeName"
                    value={fluFormData.employeeName || ""}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter employee name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Clinician Information */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Clinician Information</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinician Name: <span className="text-gray-500 font-normal text-xs">(Auto-filled)</span>
                  </label>
                  <input
                    type="text"
                    name="clinicianName"
                    value={fluFormData.clinicianName}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title: <span className="text-gray-500 font-normal text-xs">(Auto-filled)</span>
                  </label>
                  <input
                    type="text"
                    name="clinicianTitle"
                    value={fluFormData.clinicianTitle}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Vaccination Decision */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Vaccination Decision</h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="alreadyReceived"
                      checked={fluFormData.alreadyReceived}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Already Received Vaccination:</span>
                  </label>
                  {fluFormData.alreadyReceived && (
                    <div className="ml-6 mt-2 grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Location:
                        </label>
                        <input
                          type="text"
                          name="alreadyReceivedLocation"
                          value={fluFormData.alreadyReceivedLocation}
                          onChange={handleInputChange}
                          className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date:
                        </label>
                        <DateInput
                          name="alreadyReceivedDate"
                          value={fluFormData.alreadyReceivedDate}
                          onChange={handleInputChange}
                          className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="wouldLikeToReceive"
                      checked={fluFormData.wouldLikeToReceive}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Would like to receive Influenza Vaccination through Infinite Home Health (Fee $15.00)</span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="refuseDecline"
                      checked={fluFormData.refuseDecline}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Refuse/Decline to Receive Influenza Vaccination due to:</span>
                  </label>
                  {fluFormData.refuseDecline && (
                    <div className="ml-6 mt-3 space-y-2">
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="refuseReasonAllergies"
                            checked={fluFormData.refuseReasonAllergies}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Allergies</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="refuseReasonIllness"
                            checked={fluFormData.refuseReasonIllness}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Illness</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="refuseReasonReligious"
                            checked={fluFormData.refuseReasonReligious}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Religious</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="refuseReasonBeliefs"
                            checked={fluFormData.refuseReasonBeliefs}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Beliefs</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="refuseReasonPersonalPreference"
                            checked={fluFormData.refuseReasonPersonalPreference}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Personal Preference</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="refuseReasonOther"
                            checked={fluFormData.refuseReasonOther}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Other:</span>
                        </label>
                      </div>
                      {fluFormData.refuseReasonOther && (
                        <div className="mt-2">
                          <input
                            type="text"
                            name="refuseReasonOtherText"
                            value={fluFormData.refuseReasonOtherText}
                            onChange={handleInputChange}
                            className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            placeholder="Please specify..."
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Refusal Acknowledgment */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Refusal Acknowledgment</h3>

              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-4">
                  I know that my failure to follow these recommendations for vaccination may endanger my health or the health of people I come in contact with. I know that, even though I refuse to be vaccinated now, I can change my mind at any time and accept vaccination in the future. I acknowledge that I have read this Flu Vaccination form in its entirety and fully understand it.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Signature:
                    </label>
                    <input
                      type="text"
                      name="refusalAcknowledgmentSignature"
                      value={fluFormData.refusalAcknowledgmentSignature}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date:
                    </label>
                    <DateInput
                      name="refusalAcknowledgmentDate"
                      value={fluFormData.refusalAcknowledgmentDate}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Consent Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Flu Vaccination Consent</h3>

              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-4">
                  I consent to the administration of the Influenza Vaccine, and am aware that some people experience pain at the site of the injection and some may experience fever. I testify that I have none of the following conditions: severe allergy to hen's eggs, severe reaction to flu shot in the past, history of Guillain-Barre Syndrome (GBS) in the 6 weeks after getting a flu shot,
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Print Name:
                    </label>
                    <input
                      type="text"
                      name="printName"
                      value={fluFormData.printName}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clinician Signature:
                    </label>
                    <input
                      type="text"
                      name="clinicianSignature"
                      value={fluFormData.clinicianSignature}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location Providing Influenza Vaccine:
                  </label>
                  <input
                    type="text"
                    name="locationProvidingVaccine"
                    value={fluFormData.locationProvidingVaccine}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site of Injection:
                    </label>
                    <input
                      type="text"
                      name="siteOfInjection"
                      value={fluFormData.siteOfInjection}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Administered by:
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="administeredByMD"
                          checked={fluFormData.administeredByMD}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">MD</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="administeredByRN"
                          checked={fluFormData.administeredByRN}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">RN</span>
                      </label>
                      <input
                        type="text"
                        name="administeredByName"
                        value={fluFormData.administeredByName}
                        onChange={handleInputChange}
                        placeholder="Name"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lot #:
                    </label>
                    <input
                      type="text"
                      name="lotNumber"
                      value={fluFormData.lotNumber}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiration Date:
                    </label>
                    <DateInput
                      name="expirationDate"
                      value={fluFormData.expirationDate}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t border-gray-300">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
              >
                Submit/Print
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setShowUserList(true);
                  resetForm();
                  setSelectedPosition("");
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FluVaccinationForm;

